import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Users, Award, Scale, Building2, FileCheck, Phone, Mail, MapPin } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="about-page">
      <Navbar />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900">
        <div className="container-app py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white">
              About <span className="text-amber-400">LegalHub</span>
            </h1>
            <p className="mt-4 text-slate-300 text-lg">
              India's trusted platform for connecting clients with verified legal professionals
            </p>
          </motion.div>
        </div>
      </div>
      
      <div className="container-app py-12">
        {/* Mission Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-8 border border-slate-200 mb-8"
        >
          <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
            <Scale className="w-7 h-7 text-blue-600" />
            Our Mission
          </h2>
          <p className="text-slate-600 leading-relaxed">
            LegalHub was founded with a simple mission: to make legal services accessible to every Indian. 
            We believe that quality legal assistance should not be a privilege but a right accessible to all. 
            Our platform bridges the gap between clients seeking legal help and verified advocates across India, 
            making the process of finding and consulting with lawyers simple, transparent, and trustworthy.
          </p>
        </motion.div>
        
        {/* Company Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl p-8 border border-slate-200 mb-8"
        >
          <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
            <Building2 className="w-7 h-7 text-blue-600" />
            Company Information
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Company Name</p>
                <p className="font-semibold text-slate-900">LegalHub</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Location</p>
                <p className="font-semibold text-slate-900">Mumbai, Maharashtra, India</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Registered Office</p>
                <p className="font-semibold text-slate-900">
                  Mumbai, Maharashtra<br />
                  India
                </p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Contact</p>
                <p className="font-semibold text-slate-900">info@legalhubs.info</p>
                <p className="font-semibold text-slate-900">deepakrao1983@gmail.com</p>
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Why Choose Us */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl p-8 border border-slate-200 mb-8"
        >
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Why Choose LegalHub?</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-blue-50 rounded-xl">
              <Shield className="w-12 h-12 text-blue-600 mx-auto mb-4" />
              <h3 className="font-semibold text-slate-900 mb-2">Verified Lawyers</h3>
              <p className="text-sm text-slate-600">
                All advocates are verified with Bar Council of India registration before listing
              </p>
            </div>
            <div className="text-center p-6 bg-amber-50 rounded-xl">
              <Award className="w-12 h-12 text-amber-600 mx-auto mb-4" />
              <h3 className="font-semibold text-slate-900 mb-2">Transparent Pricing</h3>
              <p className="text-sm text-slate-600">
                Clear consultation fees displayed upfront with no hidden charges
              </p>
            </div>
            <div className="text-center p-6 bg-emerald-50 rounded-xl">
              <Users className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
              <h3 className="font-semibold text-slate-900 mb-2">Client Reviews</h3>
              <p className="text-sm text-slate-600">
                Genuine reviews from verified clients to help you make informed decisions
              </p>
            </div>
          </div>
        </motion.div>
        
        {/* Compliance Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-blue-900 text-white rounded-2xl p-8 mb-8"
        >
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
            <FileCheck className="w-7 h-7 text-amber-400" />
            Legal Compliance
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-amber-400 mb-2">Bar Council of India Compliance</h3>
              <p className="text-blue-100 text-sm leading-relaxed">
                All advocates listed on LegalHub are required to comply with the rules and regulations 
                set by the Bar Council of India. We verify Bar Council enrollment numbers before approving 
                any lawyer profile.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-amber-400 mb-2">Advocates Act, 1961</h3>
              <p className="text-blue-100 text-sm leading-relaxed">
                Lawyers on our platform must adhere to the Advocates Act, 1961, including professional 
                conduct standards, fee regulations, and ethical guidelines prescribed for legal practitioners 
                in India.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-amber-400 mb-2">Digital Personal Data Protection Act, 2023</h3>
              <p className="text-blue-100 text-sm leading-relaxed">
                We are committed to protecting your personal data in compliance with India's DPDP Act, 2023. 
                Your information is securely stored and processed with explicit consent.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-amber-400 mb-2">Information Technology Act, 2000</h3>
              <p className="text-blue-100 text-sm leading-relaxed">
                Our platform adheres to the IT Act and its amendments, ensuring secure transactions, 
                data protection, and compliance with intermediary guidelines.
              </p>
            </div>
          </div>
        </motion.div>
        
        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
        >
          {[
            { value: '500+', label: 'Verified Lawyers' },
            { value: '18+', label: 'Legal Categories' },
            { value: '50+', label: 'Cities Covered' },
            { value: '10,000+', label: 'Consultations' }
          ].map((stat, idx) => (
            <div key={idx} className="bg-white rounded-xl p-6 text-center border border-slate-200">
              <p className="text-3xl font-bold text-blue-600">{stat.value}</p>
              <p className="text-sm text-slate-500 mt-1">{stat.label}</p>
            </div>
          ))}
        </motion.div>
        
        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-center bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-10"
        >
          <h2 className="text-2xl font-bold text-white mb-4">Ready to Find Your Legal Expert?</h2>
          <p className="text-blue-100 mb-6">
            Connect with verified lawyers across India for your legal needs
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/lawyers"
              className="px-8 py-3 bg-white text-blue-600 font-semibold rounded-full hover:bg-blue-50 transition-colors"
              data-testid="find-lawyer-cta"
            >
              Find a Lawyer
            </Link>
            <Link
              to="/register/lawyer"
              className="px-8 py-3 bg-amber-500 text-white font-semibold rounded-full hover:bg-amber-600 transition-colors"
              data-testid="register-lawyer-cta"
            >
              Register as Lawyer
            </Link>
          </div>
        </motion.div>
      </div>
      
      <Footer />
    </div>
  );
};

export default AboutPage;
